/**
 * 처리창 겹침 가드 — $j.F.Admin.TaskGuard
 *
 * 한 건을 열어 처리하는 창(판정창·심사창…)은 **열기 전에 원장을 본다.**
 * 담당·처리 상태는 원장(g5_admin_tasks)에만 있어서, 남이 붙잡고 있는 건을 모르고 겹쳐
 * 처리하는 것을 여기서 막는다.
 *
 *   if (!await $j.F.Admin.TaskGuard.check('collusion', caseNum)) return;
 *
 * **여는 것만으로는 담당이 되지 않는다.** 보기만 하려고 여는 일도 잦기 때문이다 —
 * 맡을지는 열 때 **사람에게 묻는다**. 묻지 않고 붙이면 보러 들어간 사람이 남의 일을 떠안는다.
 *
 * 규칙 다섯.
 *   내가 맡고 있다   → 묻지 않는다. 이미 내 일이라 물을 것이 없다
 *   남이 맡고 있다   → **어떻게 열지** 묻는다. 막지는 않는다 — 휴가·퇴근으로 넘겨받는 일이 있다.
 *                     "이어서 처리" 면 담당이 나로 바뀌고(넘겨받기), "보기만" 이면 담당은 그대로
 *                     둔 채 연다 — 진행 상황만 확인하러 여는 일이 잦다
 *   아무도 안 맡았다 → **맡을지** 묻는다. 어느 쪽을 골라도 창은 열린다 —
 *                     맡기로 하면 담당이 붙고, 보기만 하면 담당은 비어 있는 채다
 *   이미 닫혔다      → 묻지 않는다. 담당은 "지금 누가 보고 있나" 라 닫힌 건엔 뜻이 없다
 *   원장에 없다/못 읽는다 → 그냥 연다. 막을 근거가 없을 때 화면이 멈추는 것이 더 나쁘다
 *
 * 모달마다 복붙하지 않는다 — 같은 물음을 두 벌로 두면 문구와 판단이 갈린다.
 */
export default class TaskGuard {

    /**
     * 「보기만」으로 연 건 — `type:refId`. 처리창 머리줄(TaskBadge)이 이걸 보고 처리 버튼을 잠근다.
     * 담당을 그대로 두고 열었다는 것은 **처리하지 않겠다**는 뜻이라, 창 안에서 처리를 누르면
     * 넘겨받을지 먼저 묻는다. 같은 건을 다시 열면(check) 새로 정한다.
     */
    static _view = new Set();

    static isViewOnly(type, refId) {
        return this._view.has(`${type}:${refId}`);
    }

    static clearViewOnly(type, refId) {
        this._view.delete(`${type}:${refId}`);
    }

    /**
     * @param {string} type  원장 종류 코드 (BadgeSource::TYPE)
     * @param {number} refId 소스 행 번호
     * @param {{onClaimed?:Function, lockOnView?:boolean}} [opts]
     *        lockOnView: 담당이 없는 건을 「열람」으로 열었거나 원장에서 닫힌 건도 보기 전용으로 잠근다.
     *        처리가 되돌려지지 않는 창만 켠다(탈퇴). 기본은 잠그지 않는다. 소스까지 끝난 건이면
     *        창이 clearViewOnly 로 푼다 — 원장만 보는 여기서는 알 수 없다.
     *        onClaimed: **담당이 바뀌었을 때만** 부른다(맡기·넘겨받기).
     *        이 창을 연 목록이 담당칸을 다시 그려야 하기 때문이다 — 여기서 담당을 붙여놓고
     *        알리지 않으면, 창을 닫아도 목록은 '미배정' 인 채로 남는다.
     * @return {Promise<boolean>} 열지 말지
     */
    static async check(type, refId, opts = {}) {
        this.clearViewOnly(type, refId);

        let t = null;
        try {
            const r = await $.post('/adm/ajax/tasks/lookup', { type, ref: refId });
            t = r && r.task;
        } catch (e) {
            return true;   // 원장을 못 읽는 것으로 처리를 막지는 않는다
        }

        if (!t) return true;                                    // 원장 밖의 건
        if (t.state === 'done' || t.state === 'skip') {              // 이미 닫힘
            if (opts.lockOnView) this._view.add(`${type}:${refId}`);
            return true;
        }
        if (t.is_mine) return true;                             // 이미 내 일 — 물을 것이 없다

        if (t.assignee) {   // 남이 맡고 있다 (내 것은 위에서 걸렀다)
            // 둘 중 하나 — 넘겨받기 / 보기만(담당 그대로). 버튼 두 개를 좌우로 세운다.
            // 「보기만」이 없으면 상황만 보려는 사람이 담당을 빼앗거나 아예 못 연다.
            // 창을 닫으면(✕·바깥·ESC) 안 연다 — 닫기는 onCancel 을 부르지 않아 view 와 갈린다
            let view = false;
            const take = await $j.Ui.Dialog.open({
                maxWidth:  400,
                title:     `${t.assignee_name} 님이 처리중입니다.`,
                confirm:   '이어서 처리',
                cancel:    '열람',
                onCancel:  () => { view = true; },
            });
            if (view) { this._view.add(`${type}:${refId}`); return true; }
            if (!take) return false;

            // 이어서 처리한다 = 이제 내가 보고 있다. 담당을 넘겨받는다
            const r = await $.post('/adm/ajax/tasks/takeover', { num: t.num });
            if (r && r.message) $j.Ui.Toast.show(r.message, { type: 'success' });
            if (typeof opts.onClaimed === 'function') opts.onClaimed();
            return true;
        }

        // 아무도 안 맡았다 — 맡을지 묻는다. 어느 쪽을 골라도 창은 열린다
        const mine = await $j.Ui.Dialog.confirm('아직 아무도 맡지 않았습니다.', {
            maxWidth: 400,
            desc:     '맡으면 다른 사람에게 "처리중" 으로 보입니다.\n맡지 않고 열람만 할 수도 있습니다.',
            confirm:  '담당',
            cancel:   '열람',
        });
        if (mine) {
            const r = await $.post('/adm/ajax/tasks/claim', { num: t.num });
            if (r && r.message) $j.Ui.Toast.show(r.message, { type: r.result ? 'success' : 'warn' });
            if (typeof opts.onClaimed === 'function') opts.onClaimed();
        } else if (opts.lockOnView) {
            this._view.add(`${type}:${refId}`);
        }

        return true;
    }

    static _esc(s) {
        return String(s == null ? '' : s).replace(/[&<>"']/g, (c) =>
            ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]));
    }
}
